import { Link } from 'react-router-dom';

interface ImageClickHandler {
  (src: string, alt: string): void;
}

export function IntroSection({ onImageClick }: { onImageClick: ImageClickHandler }) {
  return (
    <section id="intro" className="py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-10">
          <img
            src="https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/7c195a30-00fa-4ce2-bac0-5635931d265b_compressed_R.webp"
            alt="「パタカラ®」を安心して正しくご活用いただくためのイラスト"
            className="w-full max-w-3xl mx-auto h-auto object-contain rounded-xl shadow-sm cursor-pointer"
            onClick={() => onImageClick(
              'https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/7c195a30-00fa-4ce2-bac0-5635931d265b_compressed_R.webp',
              '「パタカラ®」を安心して正しくご活用いただくためのイラスト'
            )}
          />
        </div>

        <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-800 mb-8 text-center">
          「パタカラ®」を安心して正しくご活用いただくために
        </h2>

        <div className="space-y-5 text-gray-700 leading-relaxed text-base">
          <p>
            「パタカラ®」は、株式会社パックスシステムズが保有する登録商標です。
          </p>
          <p>
            株式会社パックスシステムズでは、介護・医療・口腔ケアなどの現場で「パタカラ®」を安心してご活用いただくため、名称・公式ロゴの取り扱い、指定役務としての利用および公式施設ライセンスについてご案内しています。
          </p>
          <p>
            近年、介護施設、デイサービス、歯科医院、医療機関等のWEBサイト、パンフレット、施設内資料、介護情報サイトなどにおいて、「パタカラ」の名称が使用される機会が増えています。
          </p>
          <p>
            多くの皆様に関心をお寄せいただいていることを大変うれしく思う一方で、「パタカラ®」が登録商標であることをご存じないまま、施設のサービス、口腔機能向上プログラム、広告・広報などに使用されているケースも確認されています。
          </p>
          <p>
            介護・医療施設等で「パタカラ®」を実施・提供・掲載される場合は、事前に株式会社パックスシステムズへご相談いただくか、公式施設ライセンスをご取得ください。
          </p>
          <p>
            公式ライセンスを取得することで、適切な許諾のもと、「パタカラ®」を安心して継続的にご活用いただけます。
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <a
            href="/"
            className="px-8 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer text-center"
          >
            公式施設ライセンスを見る
          </a>
          <a
            href="/contact"
            className="px-8 py-3 border-2 border-orange-500 text-orange-600 font-semibold rounded-full hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer text-center"
          >
            利用について相談する
          </a>
        </div>
      </div>
    </section>
  );
}

export function RegistrationSection({ onImageClick }: { onImageClick: ImageClickHandler }) {
  return (
    <section id="registration" className="py-16 md:py-20 bg-white border-t border-gray-100">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-10">
          <img
            src="https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/ddca0490-ea87-4f21-b120-8e05291125c0_compressed_R.webp"
            alt="「パタカラ®」は株式会社パックスシステムズの登録商標です"
            className="w-full max-w-3xl mx-auto h-auto object-contain rounded-xl shadow-sm cursor-pointer"
            onClick={() => onImageClick(
              'https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/ddca0490-ea87-4f21-b120-8e05291125c0_compressed_R.webp',
              '「パタカラ®」は株式会社パックスシステムズの登録商標です'
            )}
          />
        </div>

        <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-800 mb-8 text-center">
          「パタカラ®」は株式会社パックスシステムズの登録商標です
        </h2>

        <p className="text-gray-700 leading-relaxed text-base mb-8 text-center">
          「パタカラ®」は、株式会社パックスシステムズが保有し、商標法によって保護されている登録商標です。
        </p>

        <div className="bg-white rounded-2xl p-8 shadow-sm max-w-2xl mx-auto">
          <h3 className="text-lg font-bold text-slate-800 mb-6 text-center">商標登録情報</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-100 flex-shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-orange-500 text-sm"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500">商標登録番号</p>
                <p className="text-sm font-bold text-slate-800">第5657576号</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-100 flex-shrink-0 mt-0.5">
                <i className="ri-trademark-line text-orange-500 text-sm"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500">登録商標</p>
                <p className="text-sm font-bold text-slate-800">パタカラ</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-100 flex-shrink-0 mt-0.5">
                <i className="ri-building-line text-orange-500 text-sm"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500">権利者</p>
                <p className="text-sm font-bold text-slate-800">株式会社パックスシステムズ</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-100 flex-shrink-0 mt-0.5">
                <i className="ri-file-list-line text-orange-500 text-sm"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500">指定商品・役務</p>
                <p className="text-sm font-bold text-slate-800">第9類、第41類、第44類</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ScopeSection({ onImageClick }: { onImageClick: ImageClickHandler }) {
  return (
    <section id="scope" className="py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-10">
          <img
            src="https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/78809ccb-bc2c-4a2f-a0b6-94ee7fa5ba70_compressed_R.webp"
            alt="「パタカラ®」の商標権が及ぶ範囲について"
            className="w-full max-w-3xl mx-auto h-auto object-contain rounded-xl shadow-sm cursor-pointer"
            onClick={() => onImageClick(
              'https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/78809ccb-bc2c-4a2f-a0b6-94ee7fa5ba70_compressed_R.webp',
              '「パタカラ®」の商標権が及ぶ範囲について'
            )}
          />
        </div>

        <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-800 mb-8 text-center">
          「パタカラ®」の商標権が及ぶ範囲について
        </h2>

        <div className="space-y-5 text-gray-700 leading-relaxed text-base mb-10">
          <p>
            「パタカラ®」の商標権は、WEBサイト、パンフレット、広告等に名称やロゴを掲載する行為だけを対象とするものではありません。
          </p>
          <p>
            当社が保有する登録商標には、第9類、第41類および第44類の指定商品・指定役務が含まれています。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div
            className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onImageClick(
              'https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/1a1ccc33-0bad-406a-812a-15d7399f5c93_compressed_9.webp',
              '第9類 プログラム・音楽・画像に関する主な商品'
            )}
          >
            <img
              src="https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/1a1ccc33-0bad-406a-812a-15d7399f5c93_compressed_9.webp"
              alt="第9類 プログラム・音楽・画像に関する主な商品"
              className="w-full h-auto object-contain"
            />
          </div>
          <div
            className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onImageClick(
              'https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/cf5145c6-f535-4475-bbd6-12bb4efc27e1_compressed_41.webp',
              '第41類 音楽・教育・施設に関する主な役務'
            )}
          >
            <img
              src="https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/cf5145c6-f535-4475-bbd6-12bb4efc27e1_compressed_41.webp"
              alt="第41類 音楽・教育・施設に関する主な役務"
              className="w-full h-auto object-contain"
            />
          </div>
          <div
            className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onImageClick(
              'https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/de6883be-0622-4a04-aedc-d2e87c000bf9_compressed_44.webp',
              '第44類 介護・医療・健康に関する主な役務'
            )}
          >
            <img
              src="https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/de6883be-0622-4a04-aedc-d2e87c000bf9_compressed_44.webp"
              alt="第44類 介護・医療・健康に関する主な役務"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        <div className="space-y-5 text-gray-700 leading-relaxed text-base">
          <p>
            そのため、「パタカラ®」の名称や公式ロゴを表示する場合だけでなく、介護・医療・音楽等の指定役務として「パタカラ」を用いたサービスやプログラムを実施・提供する行為についても、パタカラ公式ライセンスを取得して安心して「パタカラ®」をご利用ください。
          </p>
          <p>
            介護・医療施設等において、口腔機能の維持・向上を目的として利用者に「パタカラ」を発声させること、音楽・映像・カラオケ等を利用して「パタカラ」を実施すること、またはこれらを施設のサービスやプログラムとして提供する場合は、事前に当社の許諾または公式施設ライセンスが必要です。
          </p>
          <p>
            許諾を受けずに「パタカラ®」を指定役務として実施・提供・掲載した場合は、商標権の侵害となる可能性があり、実施方法の見直し、掲載の停止、名称の変更等をお願いすることがあります。
          </p>
          <p>
            なお、家庭内における個人的な発声など、事業または施設サービスとして提供されない私的な利用を制限するものではありません。
          </p>
          <p>
            商標権の対象となるかどうかは、提供されるサービスの内容、利用目的、使用方法、表示方法等を踏まえて個別に確認します。
          </p>
        </div>
      </div>
    </section>
  );
}

export function UsageExamplesSection({ onImageClick }: { onImageClick: ImageClickHandler }) {
  const examples = [
    { icon: 'ri-home-heart-line', title: '介護・医療施設での実施', desc: '介護施設、デイサービス、歯科医院、医療機関等において、「パタカラ」を口腔機能の維持・向上を目的とするサービスやプログラムとして実施する場合' },
    { icon: 'ri-mic-line', title: '利用者への発声指導', desc: '施設が提供する介護・医療・口腔ケア等のサービスとして、利用者に「パタカラ」を発声させる場合' },
    { icon: 'ri-music-2-line', title: '音楽・映像を使用した実施', desc: '音楽、映像、CD、DVD、動画、配信コンテンツ等を使用して「パタカラ」を実施する場合' },
    { icon: 'ri-voice-recognition-line', title: 'カラオケを使用した実施', desc: '介護・医療施設等において、カラオケ機器やカラオケ音源を使用して「パタカラ」を実施する場合' },
    { icon: 'ri-award-line', title: 'サービス名・プログラム名としての使用', desc: '「パタカラ体操」「パタカラ教室」「パタカラプログラム」などの名称で、施設のサービスやプログラムを実施・提供する場合' },
    { icon: 'ri-global-line', title: 'WEBサイト・ブログへの掲載', desc: '「当施設ではパタカラを実施しています」など、施設のサービスや取り組みとしてWEBサイトやブログに掲載する場合' },
    { icon: 'ri-file-paper-line', title: 'パンフレット・施設内資料への掲載', desc: 'パンフレット、チラシ、プログラム表、院内資料、壁面掲示物、配布物等に「パタカラ®」の名称または公式ロゴを掲載する場合' },
    { icon: 'ri-advertisement-line', title: 'ポータルサイト・広告での使用', desc: '介護施設検索サイト、施設紹介ページ、広告、SNS等に、施設のサービス内容として「パタカラ」を掲載する場合' },
  ];

  return (
    <section id="usage-examples" className="py-16 md:py-20 bg-white border-t border-gray-100">
      <div className="max-w-5xl mx-auto px-6">
        <div className="mb-10">
          <img
            src="https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/300fbb67-939d-45a7-854f-b1800cb9b24f_compressed_unnamed.webp"
            alt="公式ライセンスが必要となる主な利用例"
            className="w-full max-w-3xl mx-auto h-auto object-contain rounded-xl shadow-sm cursor-pointer"
            onClick={() => onImageClick(
              'https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/300fbb67-939d-45a7-854f-b1800cb9b24f_compressed_unnamed.webp',
              '公式ライセンスが必要となる主な利用例'
            )}
          />
        </div>

        <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-800 mb-4 text-center">
          公式ライセンスが必要となる主な利用例
        </h2>
        <p className="text-gray-600 text-center mb-12 text-base">
          次のような形で「パタカラ®」を実施・提供・掲載する場合は、公式施設ライセンスをご取得いただくか、事前に当社へご相談ください。
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {examples.map((item, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-100 flex-shrink-0">
                <i className={`${item.icon} text-orange-500 text-lg`}></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-600 mt-8 text-sm">
          個別の利用方法がライセンスの対象となるか分からない場合は、お気軽にお問い合わせください。
        </p>
      </div>
    </section>
  );
}

export function LicenseSection({ onImageClick }: { onImageClick: ImageClickHandler }) {
  return (
    <section id="license" className="py-16 md:py-20 bg-orange-50/50 border-t border-gray-100">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-10">
          <img
            src="https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/66b9ddf7-e422-47a0-bfa2-6b3a507e146c_compressed_R.webp"
            alt="安心して使える「パタカラ®」公式施設ライセンス"
            className="w-full max-w-3xl mx-auto h-auto object-contain rounded-xl shadow-sm cursor-pointer"
            onClick={() => onImageClick(
              'https://storage.helloreaddy.io/project_files/b895c287-4223-4b9b-9456-2e4b57b1eb08/66b9ddf7-e422-47a0-bfa2-6b3a507e146c_compressed_R.webp',
              '安心して使える「パタカラ®」公式施設ライセンス'
            )}
          />
        </div>

        <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-800 mb-8 text-center">
          安心して使える「パタカラ®」公式施設ライセンス
        </h2>

        <p className="text-gray-700 leading-relaxed text-base mb-8 text-center">
          株式会社パックスシステムズでは、介護・医療施設の皆様が「パタカラ®」を安心してご利用いただけるよう、公式施設ライセンス制度を設けています。
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-8 text-center">
          公式施設ライセンスをご契約いただくことで、次の内容をご利用いただけます。
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {[
            '指定役務としての「パタカラ®」の実施・提供',
            '「パタカラ®」の名称および公式ロゴの利用',
            'WEBサイト、パンフレット、チラシ、施設内資料等への掲載',
            '公式施設認定証の発行',
            '公式ライセンス取得施設であることの表示',
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 bg-orange-50/60 rounded-lg p-4">
              <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line"></i>
              </div>
              <p className="text-sm text-gray-800 font-medium leading-relaxed">{item}</p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border border-orange-100 mb-10">
          <h3 className="text-lg font-bold text-slate-800 mb-6 text-center">ご利用料金</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="text-center bg-white rounded-xl p-5">
              <p className="text-xs text-gray-500 mb-1">1施設・1事業所あたり</p>
              <p className="text-2xl font-black text-orange-500">月額770円<span className="text-sm font-normal text-gray-500">（税込）</span></p>
            </div>
            <div className="text-center bg-white rounded-xl p-5">
              <p className="text-xs text-gray-500 mb-1">初期費用 / 更新料</p>
              <p className="text-2xl font-black text-slate-800">0円</p>
            </div>
            <div className="text-center bg-white rounded-xl p-5">
              <p className="text-xs text-gray-500 mb-1">契約期間の縛り / 解約金</p>
              <p className="text-2xl font-black text-slate-800">なし</p>
            </div>
            <div className="text-center bg-white rounded-xl p-5">
              <p className="text-xs text-gray-500 mb-1">認定証発行</p>
              <p className="text-lg font-bold text-slate-800">最短2営業日</p>
            </div>
            <div className="text-center bg-white rounded-xl p-5 sm:col-span-2 lg:col-span-2">
              <p className="text-sm text-gray-700 leading-relaxed">
                公式施設ライセンスを取得することで、適切な許諾のもと、「パタカラ®」の実施、名称および公式ロゴを安心してご活用いただけます。
              </p>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mb-6">
          ※事前申込みの内容を確認後、メールで会費ペイの登録方法をご案内します。
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/license-application"
            className="px-8 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer text-center"
          >
            公式施設ライセンスを申し込む
          </Link>
          <Link
            to="/"
            className="px-8 py-3 border-2 border-orange-500 text-orange-600 font-semibold rounded-full hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer text-center"
          >
            ライセンスの詳細を見る
          </Link>
        </div>
      </div>
    </section>
  );
}

export function CurrentUsersSection() {
  return (
    <section id="current-users" className="py-16 md:py-20 bg-white border-t border-gray-100">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-800 mb-8 text-center">
          現在すでに「パタカラ」を利用されている施設様へ
        </h2>

        <div className="space-y-5 text-gray-700 leading-relaxed text-base mb-8">
          <p>
            現在すでに、施設のサービス、口腔機能向上プログラム、WEBサイト、パンフレット、施設内資料等で「パタカラ」を利用されている場合は、現在の利用方法について速やかに当社へご相談ください。
          </p>
          <p>
            お問い合わせの際は、次の情報をお知らせください。
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <ul className="space-y-3">
            {[
              '法人名・施設名',
              '利用目的',
              '実施しているサービスやプログラムの内容',
              '使用している媒体',
              '掲載されているWEBサイトのURL',
              'パンフレットや資料等の画像・データ',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-gray-700 leading-relaxed text-base mb-8">
          内容を個別に確認したうえで、公式施設ライセンスの適用または必要な対応をご案内します。
        </p>

        <div className="flex items-center justify-center">
          <a
            href="/contact"
            className="px-8 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer text-center"
          >
            現在の利用について相談する
          </a>
        </div>
      </div>
    </section>
  );
}

export function GovernmentSection() {
  return (
    <section id="government" className="py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-800 mb-8 text-center">
          自治体・官公庁等の皆様へ
        </h2>

        <div className="space-y-5 text-gray-700 leading-relaxed text-base mb-8">
          <p>
            自治体、官公庁等が、地域の健康増進、介護予防、福祉活動などの非営利の公的事業において「パタカラ®」を利用する場合は、原則として有料の施設ライセンスは必要ありません。
          </p>
          <p>
            ただし、適切な商標管理と使用範囲の確認のため、ご利用前に次の内容をご連絡ください。
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-8">
          <ul className="space-y-3">
            {[
              '自治体・団体名',
              '担当部署名',
              '担当者名',
              '利用目的',
              '実施内容',
              '使用する媒体',
              '掲載予定の内容',
              '利用予定期間',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-5 text-gray-700 leading-relaxed text-base mb-8">
          <p>
            内容を個別に確認したうえで、無料の使用許可証を発行します。
          </p>
          <p>
            許諾は、当社が確認した自治体・団体、利用目的、実施内容、使用媒体および使用範囲に限り有効です。
          </p>
          <p>
            許諾内容を第三者へ譲渡したり、許諾を受けた自治体・団体が第三者へ再許諾したりすることはできません。
          </p>
          <p>
            地域の皆様の健康づくりに、安心して「パタカラ®」をご活用いただけるよう対応します。
          </p>
        </div>

        <div className="flex items-center justify-center">
          <a
            href="/contact"
            className="px-8 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer text-center"
          >
            非営利利用について相談する
          </a>
        </div>
      </div>
    </section>
  );
}

export function CasesSection() {
  return (
    <section id="cases" className="py-16 md:py-20 bg-white border-t border-gray-100">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl md:text-3xl font-serif font-bold text-slate-800 mb-8 text-center">
          使用許諾の事例
        </h2>

        <p className="text-gray-700 leading-relaxed text-base mb-8 text-center">
          自治体・関係団体においても、利用目的、実施内容および使用媒体を個別に確認したうえで、「パタカラ®」をご活用いただいています。
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-100">
                <i className="ri-government-line text-orange-500 text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-800">静岡県焼津市</h3>
            </div>
            <p className="text-xs font-bold text-orange-600 bg-orange-100 rounded px-2 py-1 inline-block mb-3">
              非営利の健康増進・介護予防活動における使用許諾
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              静岡県焼津市が実施する「焼津ころばん体操」等における口腔体操および口腔改善啓発について、利用目的と使用媒体を確認し、「パタカラ®」の使用を無償で許諾しました。
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-100">
                <i className="ri-hospital-line text-orange-500 text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-800">公益社団法人 東京都歯科衛生士会</h3>
            </div>
            <p className="text-xs font-bold text-orange-600 bg-orange-100 rounded px-2 py-1 inline-block mb-3">
              非営利の口腔保健啓発・健康教育における使用許諾
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              口腔保健啓発・健康教育を目的とする資料について、商標表示と使用条件を確認し、「パタカラ®」の使用およびWEB掲載を許諾しました。
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-100">
                <i className="ri-heart-pulse-line text-orange-500 text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-800">熊本県熊本市</h3>
            </div>
            <p className="text-xs font-bold text-orange-600 bg-orange-100 rounded px-2 py-1 inline-block mb-3">
              介護予防・オーラルフレイル予防における使用許諾
            </p>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              熊本市南区福祉課に、地域包括ケアシステムの取組の一環として利用する「パタカラ®」の使用を無償で許諾しました。
            </p>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              高齢者の介護予防およびオーラルフレイル予防を目的として、熊本市公式ホームページ、DVD、高齢者の通いの場などで活用されています。
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
          <ul className="space-y-2 text-xs text-gray-600 leading-relaxed">
            <li>※掲載している事例は、それぞれの利用目的、実施内容、使用媒体および掲載内容について個別に確認し、許諾したものです。</li>
            <li>※許諾内容を第三者へ譲渡したり、許諾団体を通じて他の施設・団体が自由に利用したりすることはできません。</li>
            <li>※「パタカラ®」の利用をご希望の場合は、株式会社パックスシステムズへ直接お問い合わせください。</li>
          </ul>
        </div>

        <div className="flex items-center justify-center">
          <a
            href="/contact"
            className="px-8 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer text-center"
          >
            使用許諾について相談する
          </a>
        </div>
      </div>
    </section>
  );
}